import { ColumnState } from 'ag-grid-enterprise';

/**
 * Logger utility that respects environment settings
 * In production, logs are suppressed unless explicitly enabled
 */
const logger = {
  warn: (message: string, ...args: unknown[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(message, ...args);
    }
  },
  error: (message: string, ...args: unknown[]) => {
    // Always log errors, even in production
    console.error(message, ...args);
  },
  info: (message: string, ...args: unknown[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(message, ...args);
    }
  },
};

/**
 * Validates and filters column state to remove invalid sort fields
 *
 * @param columnState - Array of AG Grid column state objects
 * @param validSortFields - Array of field names that are valid for sorting
 * @returns Filtered column state with invalid sort fields removed
 */
export function filterInvalidSortFields(columnState: ColumnState[], validSortFields: string[]): ColumnState[] {
  return columnState.map(col => {
    if (col.sort && col.colId && !validSortFields.includes(col.colId)) {
      logger.warn(`Filtered out invalid sort field: ${col.colId}`);
      return { ...col, sort: null };
    }
    return col;
  });
}

/**
 * Checks if a column state array contains any invalid sort fields
 *
 * @param columnState - Array of AG Grid column state objects
 * @param validSortFields - Array of field names that are valid for sorting
 * @returns true if any invalid sort fields are found
 */
export function hasInvalidSortFields(columnState: ColumnState[], validSortFields: string[]): boolean {
  return columnState.some(col => col.sort && col.colId && !validSortFields.includes(col.colId));
}

/**
 * Loads and validates column state from localStorage
 *
 * @param storageKey - localStorage key to read from
 * @param validSortFields - Array of field names that are valid for sorting
 * @returns Validated column state or null if invalid/not found
 */
export function loadColumnState(storageKey: string, validSortFields: string[]): ColumnState[] | null {
  const savedStateRaw = localStorage.getItem(storageKey);

  if (!savedStateRaw) {
    return null;
  }

  try {
    const savedState = JSON.parse(savedStateRaw);

    if (!Array.isArray(savedState)) {
      logger.warn(`Invalid column state format in localStorage: ${storageKey}`);
      return null;
    }

    // Filter out invalid sort fields from saved state
    return filterInvalidSortFields(savedState, validSortFields);
  } catch (err) {
    logger.error(`Failed to parse saved column state from ${storageKey}:`, err);
    return null;
  }
}

/**
 * Saves column state to localStorage after filtering invalid sort fields
 *
 * @param storageKey - localStorage key to write to
 * @param columnState - Array of AG Grid column state objects
 * @param validSortFields - Array of field names that are valid for sorting
 */
export function saveColumnState(storageKey: string, columnState: ColumnState[], validSortFields: string[]): void {
  const filteredState = filterInvalidSortFields(columnState, validSortFields);
  localStorage.setItem(storageKey, JSON.stringify(filteredState));
}

/**
 * Performs one-time cleanup of invalid localStorage state
 *
 * @param storageKey - localStorage key to check and potentially clean
 * @param validSortFields - Array of field names that are valid for sorting
 * @param identifier - Optional identifier for logging (e.g., studyId)
 */
export function cleanupInvalidColumnState(storageKey: string, validSortFields: string[], identifier?: string): void {
  const savedStateRaw = localStorage.getItem(storageKey);

  if (!savedStateRaw) {
    return;
  }

  try {
    const savedState = JSON.parse(savedStateRaw);

    if (Array.isArray(savedState) && hasInvalidSortFields(savedState, validSortFields)) {
      logger.info(`Cleaning up invalid sort state${identifier ? ` for ${identifier}` : ''}`);
      // Remove the invalid state - it will be recreated with valid defaults
      localStorage.removeItem(storageKey);
    }
  } catch (err) {
    logger.error('Failed to parse saved state during cleanup:', err);
    localStorage.removeItem(storageKey);
  }
}
